<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { maskCPF, maskCNPJ } from "@/common/utils/mask";

import {
  baseAvatar,
  baseInput,
  baseSelect,
  baseTextarea,
} from "@/common/components";

import type { CompanyPayloadType } from "@/common/types/company";

const { t, tm } = useI18n();

const emit = defineEmits(["update-data", "avatar-add", "avatar-del"]);

const props = withDefaults(
  defineProps<{
    data: CompanyPayloadType | null;
    avatar?: string | null;
    title?: string;
    width?: string;
    showAvatar?: boolean;
  }>(),
  {
    width: "100%",
    showAvatar: false,
  }
);

const defaultData = {
  description: null,
  document_number: "",
  document_type: "",
  document_name: "",
  fantasy_name: "",
  name: "",
  cnae: "",
  danger: "1",
  number_of_employee: "",
};

const documentTypes = computed(() => [
  {
    option: t("common.labels.cpf"),
    value: "cpf",
  },
  {
    option: t("common.labels.cnpj"),
    value: "cnpj",
  },
  {
    option: t("common.labels.other"),
    value: "other",
  },
]);

const employeesOptions = computed(() =>
  Object.entries(
    tm("options.number_of_employees") as Record<string, string>
  ).map(([value, option]) => ({
    option,
    value,
  }))
);

const dangerOptions = computed(() =>
  Array.from({ length: 10 }, (_, value) => ({
    option: String(value + 1),
    value: String(value + 1),
  }))
);

const payload = ref<CompanyPayloadType>({ ...defaultData });

function update() {
  emit("update-data", payload.value);
}

function documentMask(value: any) {
  if (payload.value.document_type === "cpf") return maskCPF(value);

  if (payload.value.document_type === "cnpj") return maskCNPJ(value);

  return value;
}

function changeType(type: any) {
  payload.value.document_name = type;
  payload.value.document_number = documentMask(payload.value.document_number);

  if (type === "other") payload.value.document_name = "";

  update();
}

function changeAvatar(avatar: string) {
  emit("avatar-add", avatar);
}

function delAvatar() {
  emit("avatar-del");
}

watchEffect(() => {
  if (props.data) {
    const {
      cnae,
      danger,
      description,
      document_number,
      document_type,
      document_name,
      fantasy_name,
      name,
      number_of_employee,
    } = {
      ...props.data,
    };

    payload.value = {
      cnae,
      danger,
      description,
      document_number,
      document_type,
      document_name,
      fantasy_name,
      name,
      number_of_employee,
    };
  } else {
    payload.value = { ...defaultData };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <template v-if="showAvatar">
        <div class="form-avatar">
          <base-avatar
            :url="props.avatar"
            :is-edit="true"
            @change="changeAvatar"
            @del="delAvatar()"
          />
        </div>
      </template>

      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        @input="update()"
      >
        <template #label> {{ t("common.labels.corporate_reason") }} *</template>
      </base-input>

      <base-input
        v-model="payload.fantasy_name"
        :value="payload.fantasy_name"
        @input="update()"
      >
        <template #label> {{ t("common.labels.fantasy_name") }}</template>
      </base-input>

      <div class="documents">
        <template v-if="payload.document_type === 'other'">
          <div class="flex flex-row gap-4">
            <base-select
              class="flex-2"
              v-model="payload.document_type"
              :value="payload.document_type"
              :options="documentTypes"
              @change="changeType"
            >
              <template #label>
                {{ t("common.labels.document_type") }} *
              </template>
            </base-select>

            <base-input
              class="flex-3"
              v-model="payload.document_name"
              :value="payload.document_name"
              :placeholder="t('common.placeholders.describe')"
              @input="update()"
            >
              <template #label> {{ t("common.labels.name") }} *</template>
            </base-input>
          </div>

          <base-input
            class="flex-4"
            v-model="payload.document_number"
            :value="payload.document_number"
            :placeholder="t('common.labels.number')"
            :mask="documentMask"
            @input="update()"
          >
            <template #label> {{ t("common.labels.number") }} *</template>
          </base-input>
        </template>

        <template v-else>
          <div class="flex flex-row gap-4">
            <base-select
              class="flex-2"
              v-model="payload.document_type"
              :value="payload.document_type"
              :options="documentTypes"
              @change="changeType"
            >
              <template #label>
                {{ t("common.labels.document_type") }} *
              </template>
            </base-select>

            <base-input
              class="flex-3"
              v-model="payload.document_number"
              :value="payload.document_number"
              :placeholder="t('common.labels.number')"
              :mask="documentMask"
              @input="update()"
            >
              <template #label> {{ t("common.labels.number") }} *</template>
            </base-input>
          </div>
        </template>
      </div>

      <div class="flex flex-row gap-4">
        <base-input
          v-model="payload.cnae"
          :value="payload.cnae"
          @input="update()"
        >
          <template #label> {{ t("common.labels.cnae") }}</template>
        </base-input>

        <base-select
          v-model="payload.danger"
          :value="payload.danger"
          :options="dangerOptions"
          @change="update()"
        >
          <template #label> {{ t("analysis.labels.risk_degree") }}</template>
        </base-select>

        <base-select
          v-model="payload.number_of_employee"
          :value="payload.number_of_employee"
          :options="employeesOptions"
          @change="update()"
        >
          <template #label>
            {{ t("common.labels.number_of_employee") }}</template
          >
        </base-select>
      </div>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="update()"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .form-avatar {
      margin-block-end: 1.5rem;
    }
  }

  .documents {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

@media screen and (max-width: 1024px) {
  .flex-row {
    flex-direction: column;
  }

  .gap-1 {
    gap: 1rem;
  }
}
</style>
